import {cpServiceV2Get} from 'api';
import {sendSentryException} from 'utils/error-handling';
import dayjs from 'utils/dayjs';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {type RawApiResponse} from 'api/api.types';
import {
    type AggregatedRating,
    type CityRating,
    type MicroclimateRatings,
    type StarRatingPeriod,
} from 'pages/facilities/driver-feedback/star-ratings/StarRatings.types';

export interface GetStarRatingsResponse {
    aggregatedRatings: AggregatedRating[];
    cityRatings: CityRating[];
    microclimateRatings: MicroclimateRatings[];
    end: string;
    facilityId: number;
    start: string;
}

export type GetStarRatingsRawResponse = RawApiResponse<GetStarRatingsResponse>;

/**
 * Formats the request parameters for getting star ratings based on a selected period.
 *
 * @param  selectedPeriod - The period for which the star ratings are requested.
 *
 * @group DTO Formatters
 */
export const formatGetStarRatingsRequest = (
    selectedPeriod: StarRatingPeriod
) => {
    const toDate = dayjs().format('YYYY-MM-DD');
    // we subtract 1 from the value to ensure that we're including the current month/day/year in the lookback period
    const fromDate = dayjs()
        .subtract(selectedPeriod.value - 1, selectedPeriod.unit)
        .format('YYYY-MM-DD');

    return convertKeysToSnakeCase({
        toDate,
        fromDate,
        unit: selectedPeriod.unit,
    });
};

/**
 * Formats the response from the star ratings API.
 *
 * @param response - The response object from the star ratings API.
 *
 * @group DTO Formatters
 */
export const formatGetStarRatingsRepsonse = (
    response: GetStarRatingsRawResponse
): GetStarRatingsResponse => {
    const formattedResponse = camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets a facility's aggregated star ratings within a date range
 *
 * @param  facilityId
 * @param  selectedPeriod - The period for which the star ratings are requested.
 *
 * @see '/control-panel-service/v2/facilities/{facilityId}/star-ratings'
 *
 */
const getStarRatings = async (
    facilityId: number,
    selectedPeriod: StarRatingPeriod
) => {
    try {
        const params = formatGetStarRatingsRequest(selectedPeriod);

        const response = await cpServiceV2Get<GetStarRatingsRawResponse>(
            `facilities/${facilityId}/star-ratings`,
            params
        );

        const formattedResponse = formatGetStarRatingsRepsonse(
            response.data.data
        );

        return formattedResponse;
    } catch (err) {
        sendSentryException(err);

        throw new Error(err.message);
    }
};

export default getStarRatings;
